import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { useEffect } from 'react';
import { LoadingView } from '@/components';
import { groupExpensesByCurrency } from '../../utils/currency';
import { CircleFlag } from 'react-circle-flags';
import { FileText, Globe } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReportCard, ReportCardMobile } from './components';

const currencyToCountry: Record<string, string> = {
  'USD': 'us',
  'EUR': 'eu',
  'GBP': 'gb',
  'JPY': 'jp',
  'CHF': 'ch',
  'CAD': 'ca',
  'AUD': 'au',
  'CNY': 'cn',
  'INR': 'in',
  'KRW': 'kr',
  'BRL': 'br',
  'MXN': 'mx',
};

const getCountryCode = (currency: string): string => {
  return currencyToCountry[currency.toUpperCase()] || 'un';
};

export const ReportsPage = (): React.ReactNode => {
  const { currentTrip } = useTrips();
  const { expenses, loading, error, fetchTripExpenses } = useExpenses();
  const navigate = useNavigate();
  const { tripId } = useParams<{ tripId: string }>();

  useEffect(() => {
    if (currentTrip?.id) {
      fetchTripExpenses(currentTrip.id);
    }
  }, [currentTrip?.id, fetchTripExpenses]);

  if (error) return <div>Error: {error}</div>;
  if (loading) return <LoadingView />;

  // Calculate statistics
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const plannedExpenses = expenses.filter(e => e.is_planned).reduce((sum, e) => sum + e.amount, 0);
  const actualExpenses = expenses.filter(e => !e.is_planned).reduce((sum, e) => sum + e.amount, 0);

  // Group by currency
  const currencyTotals = groupExpensesByCurrency(
    expenses.map(e => ({
      currency: e.currency || 'USD',
      amount: e.amount
    }))
  );

  // Group by category
  const categoryTotals = expenses.reduce((acc, expense) => {
    const category = expense.category;
    if (!acc[category]) {
      acc[category] = 0;
    }
    acc[category] += expense.amount;
    return acc;
  }, {} as Record<string, number>);

  const sortedCategories = Object.entries(categoryTotals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-muted-foreground">Overview of your trip expenses and statistics</p>
      </div>

      {/* Report Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Report Templates</CardTitle>
          <CardDescription>Generate specialized reports for tax and reimbursement purposes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Desktop version */}
            <div className="hidden md:block space-y-2">
              <ReportCard
                title="Refund Report (IL)"
                description="Tax refund report for Israeli regulations - ILS only"
                icon={FileText}
                onClick={() => navigate(`/trip/${tripId}/reports/refund-report-il`)}
              />
              <ReportCard
                title="Global Refund Report"
                description="Multi-currency tax refund report with currency selection"
                icon={Globe}
                onClick={() => navigate(`/trip/${tripId}/reports/refund-report-global`)}
              />
            </div>

            {/* Mobile version with more space for subtitle */}
            <div className="md:hidden space-y-3">
              <ReportCardMobile
                title="Refund Report (IL)"
                description="Tax refund report for Israeli regulations - ILS only"
                icon={FileText}
                onClick={() => navigate(`/trip/${tripId}/reports/refund-report-il`)}
              />
              <ReportCardMobile
                title="Global Refund Report"
                description="Multi-currency tax refund report with currency selection"
                icon={Globe}
                onClick={() => navigate(`/trip/${tripId}/reports/refund-report-global`)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency and Category Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Currency Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Expenses by Currency</CardTitle>
            <CardDescription>Breakdown of expenses across different currencies</CardDescription>
          </CardHeader>
          <CardContent>
            {currencyTotals.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No expenses recorded yet
              </div>
            ) : (
              <div className="space-y-4">
                {currencyTotals.map(({ currency, amount }) => (
                  <div key={currency} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CircleFlag
                        countryCode={getCountryCode(currency)}
                        height="24"
                        width="24"
                      />
                      <span className="font-medium">{currency}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{currency} {amount.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">
                        {((amount / totalExpenses) * 100).toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Top Categories</CardTitle>
            <CardDescription>Your highest spending categories</CardDescription>
          </CardHeader>
          <CardContent>
            {sortedCategories.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No expenses recorded yet
              </div>
            ) : (
              <div className="space-y-4">
                {sortedCategories.map(([category, amount]) => {
                  const percentage = (amount / totalExpenses) * 100;
                  return (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{category}</span>
                        <span className="font-bold">${amount.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground text-right">
                        {percentage.toFixed(1)}% of total
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Planned vs Actual */}
      {plannedExpenses > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Planned vs Actual</CardTitle>
            <CardDescription>Comparison of planned and actual expenses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Planned Expenses</span>
                <span className="font-bold">${plannedExpenses.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Actual Expenses</span>
                <span className="font-bold">${actualExpenses.toFixed(2)}</span>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Difference</span>
                  <span className={`font-bold ${actualExpenses > plannedExpenses ? 'text-red-500' : 'text-green-500'}`}>
                    {actualExpenses > plannedExpenses ? '+' : '-'}
                    ${Math.abs(actualExpenses - plannedExpenses).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
